import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, Play, Square, Trash2, Download, RefreshCw, HardDrive,
  Users, ArrowUpCircle, ArrowDownCircle, FileArchive, Wifi, Server,
  Rss, Plus, Check, X, Search, FolderOutput, Zap, Settings2, Send,
  Clock, CheckCircle2, XCircle, Loader2, ChevronDown, ChevronUp, Terminal, AlertTriangle
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import api from '../lib/api';

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatTimeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  return formatDate(iso);
};

const PRE_ACTIONS = [
  { value: 'none', label: 'Keine' },
  { value: 'kill_process', label: 'Prozess beenden' },
];
const POST_ACTIONS = [
  { value: 'overwrite', label: 'Überschreiben' },
  { value: 'delete_and_copy', label: 'Ziel löschen + kopieren' },
  { value: 'copy_new', label: 'Nur neue Datei kopieren' },
];

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20', label: 'Wartend' },
  dispatched: { icon: Loader2, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', label: 'Übertragen', spin: true },
  running: { icon: Loader2, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20', label: 'Läuft...', spin: true },
  completed: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20', label: 'Erfolgreich' },
  failed: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', label: 'Fehlgeschlagen' },
  cancelled: { icon: X, color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20', label: 'Abgebrochen' },
  unknown: { icon: AlertTriangle, color: 'text-gray-400', bg: 'bg-zinc-800 border-zinc-700', label: 'Unbekannt' },
};

/* ─────── Status Badge ─────── */
function StatusBadge({ status, seederActive }) {
  const c = {
    created: 'bg-zinc-700 text-zinc-300',
    seeding: seederActive ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-yellow-500/20 text-yellow-400',
    distributing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    stopped: 'bg-zinc-600 text-zinc-400',
  }[status] || 'bg-zinc-700 text-zinc-300';
  const l = { created: 'Erstellt', seeding: seederActive ? 'Seeding' : 'Seed gestoppt', distributing: 'Verteilen', stopped: 'Gestoppt' }[status] || status;
  return <Badge className={c}>{l}</Badge>;
}

/* ─────── Peer Row ─────── */
function PeerRow({ peer }) {
  const p = peer.progress || 0;
  return (
    <div className="flex items-center gap-3 text-xs py-1.5 border-b border-[#2A2A2A] last:border-0">
      <Wifi className={`w-3.5 h-3.5 flex-shrink-0 ${peer.is_seed ? 'text-green-400' : 'text-blue-400'}`} />
      <span className="text-gray-400 font-mono w-36 truncate">{peer.ip}:{peer.port}</span>
      <div className="flex-1 h-1.5 bg-[#333] rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all" style={{ width: `${p}%` }} />
      </div>
      <span className={`w-12 text-right font-medium ${p >= 100 ? 'text-green-400' : 'text-blue-400'}`}>{p}%</span>
    </div>
  );
}

/* ─────── Dispatch Status Panel ─────── */
function DispatchStatusPanel({ dispatches, sourceType, sourceId }) {
  const [expandedId, setExpandedId] = useState(null);
  const relevant = dispatches.filter(d => d.type === sourceType && d.source_id === sourceId);

  if (relevant.length === 0) return null;

  return (
    <div className="border-t border-[#2A2A2A] bg-[#141414]">
      {relevant.map(dispatch => {
        const summary = dispatch.summary || {};
        const isExpanded = expandedId === dispatch.id;
        const allDone = summary.pending === 0 && summary.dispatched === 0;
        const hasFailures = summary.failed > 0;

        return (
          <div key={dispatch.id} className="border-b border-[#2A2A2A] last:border-b-0">
            <div
              className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-white/[0.02] transition-colors"
              onClick={() => setExpandedId(isExpanded ? null : dispatch.id)}
              data-testid={`dispatch-row-${dispatch.id}`}
            >
              <div className="flex items-center gap-3">
                <Send className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-xs text-gray-400">
                  {formatTimeAgo(dispatch.dispatched_at)} an <strong className="text-white">{summary.total}</strong> Gerät{summary.total !== 1 ? 'e' : ''}
                </span>
                {dispatch.source_version && (
                  <Badge className="bg-cyan-500/15 text-cyan-400 text-[10px] px-1.5 py-0">v{dispatch.source_version}</Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Summary badges */}
                {summary.completed > 0 && (
                  <span className="flex items-center gap-1 text-[10px] text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3" />{summary.completed}
                  </span>
                )}
                {summary.failed > 0 && (
                  <span className="flex items-center gap-1 text-[10px] text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full">
                    <XCircle className="w-3 h-3" />{summary.failed}
                  </span>
                )}
                {summary.cancelled > 0 && (
                  <span className="flex items-center gap-1 text-[10px] text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded-full">
                    <X className="w-3 h-3" />{summary.cancelled}
                  </span>
                )}
                {(summary.pending > 0 || summary.dispatched > 0 || summary.running > 0) && (
                  <span className="flex items-center gap-1 text-[10px] text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">
                    <Loader2 className="w-3 h-3 animate-spin" />{(summary.pending || 0) + (summary.dispatched || 0) + (summary.running || 0)}
                  </span>
                )}
                {allDone && !hasFailures && summary.completed > 0 && (
                  <Badge className="bg-green-500/20 text-green-400 text-[10px]">Abgeschlossen</Badge>
                )}
                {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-500" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-500" />}
              </div>
            </div>

            {isExpanded && (
              <div className="px-4 pb-3 space-y-1.5">
                {(dispatch.device_statuses || []).map(ds => {
                  const cfg = STATUS_CONFIG[ds.status] || STATUS_CONFIG.unknown;
                  const Icon = cfg.icon;
                  return (
                    <DeviceStatusRow key={ds.device_id} ds={ds} cfg={cfg} Icon={Icon} />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─────── Device Status Row (inside Dispatch) ─────── */
function DeviceStatusRow({ ds, cfg, Icon }) {
  const [showOutput, setShowOutput] = useState(false);
  const hasOutput = ds.stdout || ds.stderr;
  return (
    <div>
      <div
        className={`flex items-center gap-2.5 p-2 rounded-lg border text-xs ${cfg.bg} ${hasOutput ? 'cursor-pointer' : ''}`}
        onClick={() => hasOutput && setShowOutput(!showOutput)}
        data-testid={`device-status-${ds.device_id}`}
      >
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${cfg.color} ${cfg.spin ? 'animate-spin' : ''}`} />
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ds.is_online ? 'bg-green-400' : 'bg-gray-600'}`} />
        <span className="text-white font-medium w-32 truncate">{ds.computer_name}</span>
        <span className={`${cfg.color}`}>{cfg.label}</span>
        {ds.exit_code !== undefined && ds.exit_code !== null && (
          <span className="text-gray-500 font-mono">Exit: {ds.exit_code}</span>
        )}
        {ds.completed_at && (
          <span className="text-gray-500 ml-auto">{formatTimeAgo(ds.completed_at)}</span>
        )}
        {hasOutput && (
          <Terminal className="w-3 h-3 text-gray-500 ml-auto" />
        )}
      </div>
      {showOutput && hasOutput && (
        <div className="ml-6 mt-1 p-2 bg-[#0D0D0D] rounded border border-[#333] max-h-32 overflow-auto">
          {ds.stdout && <pre className="text-[10px] text-gray-400 font-mono whitespace-pre-wrap">{ds.stdout}</pre>}
          {ds.stderr && <pre className="text-[10px] text-red-400 font-mono whitespace-pre-wrap mt-1">{ds.stderr}</pre>}
        </div>
      )}
    </div>
  );
}

/* ─────── Job Card ─────── */
function JobCard({ job, onSeed, onStop, onDelete, onDownloadTorrent, onDispatch, devices, dispatches }) {
  const [expanded, setExpanded] = useState(false);
  const [showDevices, setShowDevices] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [dispatching, setDispatching] = useState(false);
  const peers = Object.values(job.peers || {});
  const seeders = peers.filter(p => p.is_seed).length;
  const leechers = peers.filter(p => !p.is_seed).length;
  const hasDispatches = dispatches.some(d => d.type === 'job' && d.source_id === job.id);

  return (
    <div data-testid={`dist-job-${job.id}`} className="bg-[#1C1C1C] rounded-xl border border-[#2A2A2A] overflow-hidden">
      <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3 min-w-0">
          <FileArchive className="w-5 h-5 text-orange-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium truncate">{job.name}</span>
              <StatusBadge status={job.status} seederActive={job.seeder_active} />
              {hasDispatches && <Badge className="bg-blue-500/15 text-blue-400 text-[10px]">Verteilt</Badge>}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
              <span>{formatBytes(job.file_size)}</span>
              <span>{job.piece_count} Stücke</span>
              <span>{formatDate(job.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-3 text-xs mr-4">
            <span className="flex items-center gap-1 text-green-400"><ArrowUpCircle className="w-3.5 h-3.5" />{job.tracker_seeders || seeders}</span>
            <span className="flex items-center gap-1 text-blue-400"><ArrowDownCircle className="w-3.5 h-3.5" />{job.tracker_leechers || leechers}</span>
            <span className="flex items-center gap-1 text-gray-400"><Users className="w-3.5 h-3.5" />{job.total_peers || 0}</span>
          </div>
          {!job.seeder_active ? (
            <Button size="sm" onClick={(e) => { e.stopPropagation(); onSeed(job.id); }} className="bg-green-600 hover:bg-green-700 text-white text-xs"><Play className="w-3.5 h-3.5 mr-1" />Seed</Button>
          ) : (
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onStop(job.id); }} className="text-xs border-red-500/30 text-red-400"><Square className="w-3.5 h-3.5 mr-1" />Stop</Button>
          )}
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onDownloadTorrent(job.id); }} className="text-xs"><Download className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setShowDevices(!showDevices); }} className="text-xs border-blue-500/30 text-blue-400"><Server className="w-3.5 h-3.5 mr-1" />Verteilen</Button>
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onDelete(job.id); }} className="text-xs text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      {showDevices && (
        <DeviceSelector devices={devices} selected={selectedDevices} setSelected={setSelectedDevices}
          onDispatch={async () => { setDispatching(true); await onDispatch(job.id, selectedDevices); setDispatching(false); setShowDevices(false); }}
          dispatching={dispatching} />
      )}

      {/* Dispatch Status */}
      <DispatchStatusPanel dispatches={dispatches} sourceType="job" sourceId={job.id} />

      {expanded && peers.length > 0 && (
        <div className="border-t border-[#2A2A2A] p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Peers ({peers.length})</p>
          {peers.map((peer, i) => <PeerRow key={i} peer={peer} />)}
        </div>
      )}
    </div>
  );
}

/* ─────── Device Selector ─────── */
function DeviceSelector({ devices, selected, setSelected, onDispatch, dispatching }) {
  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);
  const selectAll = () => setSelected(devices.filter(d => d.is_online).map(d => d.id));
  return (
    <div className="border-t border-[#2A2A2A] p-4 bg-[#161616]">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-white">Zielgeräte auswählen</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="text-xs" onClick={selectAll}>Alle Online ({devices.filter(d => d.is_online).length})</Button>
          <Button size="sm" disabled={selected.length === 0 || dispatching} onClick={onDispatch} className="bg-blue-600 hover:bg-blue-700 text-xs text-white" data-testid="dispatch-confirm-btn">
            {dispatching ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Verteile...</> : <><Send className="w-3.5 h-3.5 mr-1" />An {selected.length} Geräte verteilen</>}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
        {devices.map(d => (
          <label key={d.id} className={`flex items-center gap-2 p-2 rounded-lg text-xs cursor-pointer transition-colors ${selected.includes(d.id) ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-[#1C1C1C] border border-[#2A2A2A]'} ${!d.is_online ? 'opacity-40' : ''}`}>
            <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggle(d.id)} disabled={!d.is_online} className="rounded border-gray-600" />
            <span className={`w-1.5 h-1.5 rounded-full ${d.is_online ? 'bg-green-400' : 'bg-gray-600'}`} />
            <span className="text-gray-300 truncate">{d.computer_name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ─────── Feed Card ─────── */
function FeedCard({ feed, devices, onCheck, onDelete, onDistribute, dispatches }) {
  const [showDevices, setShowDevices] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState(feed.target_devices || []);
  const [checking, setChecking] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const hasDispatches = dispatches.some(d => d.type === 'feed' && d.source_id === feed.id);

  const handleCheck = async () => {
    setChecking(true);
    const r = await onCheck(feed.id);
    setCheckResult(r);
    setChecking(false);
  };

  const handleDistribute = async () => {
    setDistributing(true);
    await onDistribute(feed.id, selectedDevices);
    setDistributing(false);
    setShowDevices(false);
  };

  return (
    <div data-testid={`feed-${feed.id}`} className="bg-[#1C1C1C] rounded-xl border border-[#2A2A2A] overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Rss className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">{feed.name}</span>
                {feed.current_version && <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">v{feed.current_version}</Badge>}
                {feed.auto_distribute && <Badge className="bg-purple-500/20 text-purple-400">Auto</Badge>}
                {hasDispatches && <Badge className="bg-blue-500/15 text-blue-400 text-[10px]">Verteilt</Badge>}
              </div>

              {/* Config details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                <div className="flex items-center gap-1.5">
                  <Search className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-500">Quelle:</span>
                  <span className="text-gray-400 truncate" title={feed.source_url}>{feed.source_url?.replace(/https?:\/\//, '').slice(0, 50)}...</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FolderOutput className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-500">Ziel:</span>
                  <span className="text-gray-400 font-mono truncate" title={feed.target_path}>{feed.target_filename || '...'} &rarr; {feed.target_path?.slice(-40)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Zap className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-500">Pre:</span>
                  <span className="text-gray-400">{feed.pre_action === 'kill_process' ? `${feed.pre_action_process} beenden` : 'Keine'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Settings2 className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-500">Post:</span>
                  <span className="text-gray-400">
                    {POST_ACTIONS.find(a => a.value === feed.post_action)?.label || feed.post_action}
                    {feed.post_action_process ? ` + ${feed.post_action_process} starten` : ''}
                  </span>
                </div>
                {feed.cleanup_paths?.length > 0 && (
                  <div className="flex items-center gap-1.5 col-span-2">
                    <Trash2 className="w-3 h-3 text-gray-500" />
                    <span className="text-gray-500">Bereinigen:</span>
                    <span className="text-gray-400 font-mono truncate">{feed.cleanup_paths.join(', ')}</span>
                  </div>
                )}
              </div>

              {/* Current version info */}
              {feed.current_url && (
                <div className="bg-[#161616] rounded-lg p-2.5 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Aktuelle Datei:</span>
                    <span className="text-gray-300 font-mono">{feed.current_filename} ({formatBytes(feed.current_size)})</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Zuletzt geprüft:</span>
                    <span className="text-gray-400">{formatDate(feed.last_checked)}</span>
                  </div>
                </div>
              )}

              {/* Check result */}
              {checkResult && (
                <div className={`rounded-lg p-2.5 text-xs ${checkResult.has_update ? 'bg-green-500/10 border border-green-500/20' : 'bg-zinc-800'}`}>
                  {checkResult.has_update ? (
                    <div className="flex items-center gap-2 text-green-400">
                      <Check className="w-4 h-4" />
                      <span>Neue Version gefunden: <strong>v{checkResult.latest_version}</strong> ({formatBytes(checkResult.latest_size)})</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Check className="w-4 h-4" />
                      <span>Bereits auf dem neuesten Stand (v{checkResult.latest_version})</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
            <Button size="sm" onClick={handleCheck} disabled={checking}
              className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs" data-testid={`check-feed-${feed.id}`}>
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${checking ? 'animate-spin' : ''}`} />
              {checking ? 'Prüfe...' : 'Prüfen'}
            </Button>
            <Button size="sm" onClick={() => setShowDevices(!showDevices)} disabled={!feed.current_url}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid={`distribute-feed-${feed.id}`}>
              <Send className="w-3.5 h-3.5 mr-1" />Verteilen
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(feed.id)} className="text-xs text-red-400">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {showDevices && (
        <DeviceSelector devices={devices} selected={selectedDevices} setSelected={setSelectedDevices}
          onDispatch={handleDistribute} dispatching={distributing} />
      )}

      {/* Dispatch Status */}
      <DispatchStatusPanel dispatches={dispatches} sourceType="feed" sourceId={feed.id} />
    </div>
  );
}

/* ─────── Add Feed Form ─────── */
function AddFeedForm({ onSave, onCancel }) {
  const [form, setForm] = useState({
    name: '', source_url: '', file_pattern: '',
    target_path: '', target_filename: '',
    pre_action: 'none', pre_action_process: '',
    post_action: 'overwrite', post_action_process: '',
    cleanup_paths_str: '', auto_distribute: false,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSave = () => {
    onSave({ ...form, cleanup_paths: form.cleanup_paths_str ? form.cleanup_paths_str.split('\n').map(s => s.trim()).filter(Boolean) : [] });
  };

  const inputCls = "w-full bg-[#111] border border-[#333] rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none";
  const labelCls = "text-xs text-gray-400 font-medium";

  return (
    <div className="bg-[#1C1C1C] rounded-xl border border-cyan-500/30 p-5 space-y-4">
      <div className="flex items-center gap-2 text-cyan-400 font-medium"><Plus className="w-4 h-4" />Neuen Update-Feed erstellen</div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className={labelCls}>Name</label><input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} placeholder="z.B. Regula Document Reader Database" /></div>
        <div><label className={labelCls}>Quell-URL</label><input className={inputCls} value={form.source_url} onChange={e => set('source_url', e.target.value)} placeholder="https://downloads.example.com/updates/" /></div>
        <div><label className={labelCls}>Datei-Pattern (Regex)</label><input className={inputCls} value={form.file_pattern} onChange={e => set('file_pattern', e.target.value)} placeholder="data_.*\.dat" /></div>
        <div><label className={labelCls}>Zielverzeichnis (Remote)</label><input className={inputCls} value={form.target_path} onChange={e => set('target_path', e.target.value)} placeholder="C:\Users\{USER}\AppData\Local\..." /></div>
        <div><label className={labelCls}>Ziel-Dateiname</label><input className={inputCls} value={form.target_filename} onChange={e => set('target_filename', e.target.value)} placeholder="data.dat (leer = Original)" /></div>

        <div className="space-y-2">
          <label className={labelCls}>Vor dem Kopieren (Pre-Action)</label>
          <select className={inputCls} value={form.pre_action} onChange={e => set('pre_action', e.target.value)}>
            {PRE_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          {form.pre_action === 'kill_process' && (
            <input className={inputCls} value={form.pre_action_process} onChange={e => set('pre_action_process', e.target.value)} placeholder="READERDEMO.exe" />
          )}
        </div>

        <div className="space-y-2">
          <label className={labelCls}>Nach dem Kopieren (Post-Action)</label>
          <select className={inputCls} value={form.post_action} onChange={e => set('post_action', e.target.value)}>
            {POST_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <input className={inputCls} value={form.post_action_process} onChange={e => set('post_action_process', e.target.value)} placeholder="Prozess starten nach Kopieren (z.B. READERDEMO.exe)" />
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Ordner bereinigen nach Update (pro Zeile ein Pfad)</label>
          <textarea className={inputCls + " h-16 resize-none font-mono text-xs"} value={form.cleanup_paths_str} onChange={e => set('cleanup_paths_str', e.target.value)}
            placeholder={"C:\\Users\\{USER}\\AppData\\Local\\Regula\\Document Reader SDK\\Updates\\bin"} />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} className="text-xs"><X className="w-3.5 h-3.5 mr-1" />Abbrechen</Button>
        <Button onClick={handleSave} disabled={!form.name || !form.source_url} className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs"><Check className="w-3.5 h-3.5 mr-1" />Feed erstellen</Button>
      </div>
    </div>
  );
}

/* ═════════════════ MAIN PAGE ═════════════════ */
export default function DistributionPage() {
  const [tab, setTab] = useState('feeds');
  const [jobs, setJobs] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [stats, setStats] = useState({});
  const [devices, setDevices] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showAddFeed, setShowAddFeed] = useState(false);
  const fileInputRef = useRef(null);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [jobsRes, feedsRes, statsRes, devicesRes, dispatchRes] = await Promise.all([
        api.get('/distribution/jobs'),
        api.get('/distribution/feeds'),
        api.get('/distribution/stats'),
        api.get('/agent/devices'),
        api.get('/distribution/dispatches?limit=30'),
      ]);
      setJobs(jobsRes.data.jobs || []);
      setFeeds(feedsRes.data.feeds || []);
      setStats(statsRes.data);
      setDevices(devicesRes.data || []);
      setDispatches(dispatchRes.data.dispatches || []);
    } catch (err) { console.error('Fetch error:', err); }
  }, []);

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, 10000);
    return () => clearInterval(intervalRef.current);
  }, [fetchData]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadProgress(0);
    const fd = new FormData(); fd.append('file', file); fd.append('name', file.name);
    try {
      await api.post('/distribution/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => setUploadProgress(Math.round((p.loaded / p.total) * 100)),
      });
      await fetchData();
    } catch (err) { console.error(err); }
    setUploading(false); setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Job actions
  const startSeed = async (id) => { try { await api.post(`/distribution/jobs/${id}/seed`); await fetchData(); } catch {} };
  const stopSeed = async (id) => { try { await api.post(`/distribution/jobs/${id}/stop`); await fetchData(); } catch {} };
  const deleteJob = async (id) => { try { await api.delete(`/distribution/jobs/${id}`); await fetchData(); } catch {} };
  const downloadTorrent = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/distribution/torrent/${id}`, '_blank');
  const dispatchJob = async (id, deviceIds) => { try { await api.post(`/distribution/jobs/${id}/dispatch`, { device_ids: deviceIds }); await fetchData(); } catch {} };

  // Feed actions
  const createFeed = async (data) => { try { await api.post('/distribution/feeds', data); setShowAddFeed(false); await fetchData(); } catch {} };
  const deleteFeed = async (id) => { try { await api.delete(`/distribution/feeds/${id}`); await fetchData(); } catch {} };
  const checkFeed = async (id) => { try { const r = await api.post(`/distribution/feeds/${id}/check`); await fetchData(); return r.data; } catch { return null; } };
  const distributeFeed = async (id, deviceIds) => { try { await api.post(`/distribution/feeds/${id}/distribute`, { device_ids: deviceIds }); await fetchData(); } catch {} };

  const tabCls = (t) => `px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === t ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`;

  // Count active dispatches (with pending/dispatched tasks)
  const activeDispatchCount = dispatches.filter(d => {
    const s = d.summary;
    return s && (s.pending > 0 || s.dispatched > 0);
  }).length;

  return (
    <div className="space-y-6" data-testid="distribution-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <HardDrive className="w-7 h-7 text-orange-500" />Distribution Engine
          </h1>
          <p className="text-zinc-400 mt-1">Private Dateiverteilung an Remote-Geräte</p>
        </div>
        <div className="flex items-center gap-3">
          {activeDispatchCount > 0 && (
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 animate-pulse" data-testid="active-dispatches-badge">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />{activeDispatchCount} aktiv
            </Badge>
          )}
          {tab === 'feeds' && (
            <Button onClick={() => setShowAddFeed(true)} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="add-feed-btn">
              <Plus className="w-4 h-4 mr-2" />Feed erstellen
            </Button>
          )}
          {tab === 'torrents' && (
            <>
              <input ref={fileInputRef} type="file" onChange={handleUpload} className="hidden" />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-orange-600 hover:bg-orange-700 text-white">
                <Upload className={`w-4 h-4 mr-2 ${uploading ? 'animate-bounce' : ''}`} />
                {uploading ? `${uploadProgress}%` : 'Datei hochladen'}
              </Button>
            </>
          )}
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="bg-[#1C1C1C] rounded-xl border border-orange-500/30 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-orange-400 font-medium">Hochladen...</span>
            <span className="text-sm text-orange-400 font-mono">{uploadProgress}%</span>
          </div>
          <div className="w-full h-2 bg-[#333] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { v: stats.total_feeds || 0, l: 'Update-Feeds', c: 'text-cyan-400' },
          { v: stats.total_jobs || 0, l: 'Torrents', c: 'text-orange-400' },
          { v: stats.active_seeders || 0, l: 'Aktive Seeder', c: 'text-green-400' },
          { v: stats.total_tracked_peers || 0, l: 'Tracked Peers', c: 'text-blue-400' },
          { v: formatBytes(stats.total_file_size || 0), l: 'Gesamtgröße', c: 'text-orange-400' },
        ].map((s, i) => (
          <div key={i} className="bg-[#1C1C1C] rounded-xl border border-[#2A2A2A] p-4">
            <div className={`text-2xl font-bold ${s.c}`}>{s.v}</div>
            <div className="text-xs text-zinc-500 mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#161616] rounded-xl p-1 w-fit">
        <button className={tabCls('feeds')} onClick={() => setTab('feeds')} data-testid="tab-feeds">
          <Rss className="w-4 h-4 inline mr-1.5" />Update-Feeds ({feeds.length})
        </button>
        <button className={tabCls('torrents')} onClick={() => setTab('torrents')} data-testid="tab-torrents">
          <FileArchive className="w-4 h-4 inline mr-1.5" />Torrents ({jobs.length})
        </button>
      </div>

      {/* Content */}
      {tab === 'feeds' && (
        <div className="space-y-3">
          {showAddFeed && <AddFeedForm onSave={createFeed} onCancel={() => setShowAddFeed(false)} />}
          {feeds.length === 0 && !showAddFeed ? (
            <div className="text-center py-16 text-zinc-500">
              <Rss className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Keine Update-Feeds konfiguriert</p>
              <p className="text-sm mt-1">Erstellen Sie einen Feed für automatische Versions-Updates</p>
            </div>
          ) : (
            feeds.map(feed => <FeedCard key={feed.id} feed={feed} devices={devices} onCheck={checkFeed} onDelete={deleteFeed} onDistribute={distributeFeed} dispatches={dispatches} />)
          )}
        </div>
      )}

      {tab === 'torrents' && (
        <div className="space-y-3">
          {jobs.length === 0 ? (
            <div className="text-center py-16 text-zinc-500">
              <HardDrive className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Keine Torrents vorhanden</p>
              <p className="text-sm mt-1">Laden Sie eine Datei hoch oder starten Sie eine Feed-Verteilung</p>
            </div>
          ) : (
            jobs.map(job => <JobCard key={job.id} job={job} devices={devices} onSeed={startSeed} onStop={stopSeed} onDelete={deleteJob} onDownloadTorrent={downloadTorrent} onDispatch={dispatchJob} dispatches={dispatches} />)
          )}
        </div>
      )}
    </div>
  );
}
